<template lang="pug">
y-dialog(v-if="showDialog" v-model="showDialog" max-width=568 persistent fluid scrollable content-class="round-2xl")
  v-card
    v-card-title.pa-4
      v-spacer
      .text-xl.font-bold.ml-10 {{ $t('record_payment') }}
      v-spacer
      v-btn(icon @click="showDialog = false" small color="primary")
        v-icon mdi-close
    v-divider

    v-card-text.primary--text.px-6.pt-6.pb-10
      div.neutral200.round-xl.pa-3(style="border: 1px solid var(--v-neutral300) !important;")
        v-row(no-gutters align="center")
          v-col(cols="6")
            .font-semibold.neutral700--text {{ invoice.seller.sold_by }}
            .text-sm.font-medium.neutral500--text {{ invoice.invoice_no }}
          v-col(cols="3" align="right")
            .text-xs.neutral700--text Total amount
            .font-semibold.neutral700--text {{ invoice.amount_total | displayPrice($currency) }}
          v-col(cols="3" align="right")
            .text-xs.neutral700--text Unpaid balance
            .font-semibold.danger500--text {{ invoice.amount_remaining | displayPrice($currency) }}
      v-form(ref="form")
        v-row.mt-3(no-gutters)
          v-col.pr-2
            .text-sm.font-semibold.neutral500--text {{ $t('payment_method') }}
            v-select.text-sm.round-xl.mt-2(v-model="paymentMethod" :items="['Cash', 'Card', 'Cheque', 'Online transfer', 'Other']" placeholder="Choose payment method " outlined dense height="48" :rules="rules.not_empty" hide-details)
              template(v-slot:append)
                eva-icon.mt-1(name="chevron-down-outline" width="24" height="24" :fill="$colors.basic700")
          v-col.pl-2
            .text-sm.font-semibold.neutral500--text {{ $t('amount') }}
            v-text-field.text-sm.round-xl.mt-2(v-model="amount" placeholder="0.00" outlined dense height="48" type="number" :prefix="$currency.symbol" :rules="rules.not_empty" hide-details)
        v-row.mt-3(no-gutters)
          v-col.pr-2
            .text-sm.font-semibold.neutral500--text Payment date
            y-dialog(v-model="showPicker" width='320px' persistent)
              template(v-slot:activator='{ on }')
                v-text-field.text-sm.round-xl.date-field.mt-2(v-model="paidDateField" placeholder="Payment date" outlined dense height="48" readonly :rules="rules.not_empty" hide-details="auto" v-on="on" clearable)
                  template(v-slot:append)
                    eva-icon(name="calendar-outline" fill="#848484")
              v-card
                v-card-text.px-2.pt-2.pb-0
                  yc-date-picker(:parentDate.sync="paidDate" color="info")
                v-card-actions.px-4.pb-4
                  v-row(no-gutters)
                    v-col.pr-2
                      v-btn.button-sm(color="basic700" block dark outlined depressed @click="showPicker = false") {{ $t('cancel') }}
                    v-col.pl-2
                      v-btn.button-sm(color="info500" block depressed dark @click="pickDate('paidDateField', 'showPicker', paidDate)") {{ $t('select') }}
          v-col.pl-2
            .text-sm.font-semibold.neutral500--text {{ $t('remarks') }}
            v-text-field.text-sm.round-xl.mt-2(v-model="remarks" placeholder="Enter remarks" outlined dense height="48" hide-details)
        
      .text-sm.font-semibold.neutral500--text.mt-3 {{ $t('upload_file') }}
      v-card.round-xl.pa-8.mt-2(link outlined v-if="!selectedFile" style="border-style: dashed; border-color: var(--v-neutral400)")
        .text-center
          v-avatar.mb-3(color="neutral300" size="40")
            v-icon(color="neutral500" size="18") $yzi-upload-01
          .mb-1.text-sm 
            span.font-semibold.info--text Click to upload 
            | or drag and drop
          .text-xs.neutral500--text PDF, DOCS, JPG, PNG (max. 4MB)
        input.input-change-layout(ref="file" type="file" @change="onFileChange" accept="image/png,image/jpeg,.pdf,.doc")

      template(v-else)
        v-card.d-flex.pa-3.round-xl.mt-2(outlined @mouseover="showDeleteButton = true" @mouseleave="showDeleteButton = false" :class="{ neutral300: showDeleteButton }")
          .text-sm.flex-grow-1
            .font-medium {{ selectedFile.name }}
            .neutral500--text {{ selectedFile.size | bytesToSize }}
          v-btn.my-auto(v-if="showDeleteButton" icon small @click="deleteFile")
            v-icon(color="neutral500") $yzi-trash-03
          v-icon.my-auto.ml-4(v-else size="22" color="success") $yzi-check-circle-solid

    v-divider
    v-card-actions.py-3.px-6
      v-btn.button-md.underline.px-0(@click="showDialog = false" text) {{ $t('cancel') }}
      v-spacer
      v-btn.button-md(@click="done" color="primary" depressed :loading="isLoading") {{ $t('done') }}

</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { isFileSizeOverLimit } from '@/utils'

export default {
  name: 'RecordInvoicePaymentDialog',
  props: {
    open: {
      type: Boolean,
      required: true,
    },
    id: {
      required: true,
    },
    invoice: {
      required: true,
      type: Object,
      default: null
    }
  },
  data () {
    return {
      isLoading: false,
      showDeleteButton: false,
      showPicker: false,
      amount: null,
      remarks: null,
      paymentMethod: null,
      selectedFile: null,
      paidDate: null,
      paidDateField: null,
      rules: {
        not_empty: [v => (v != null && v.toString().trim().length) || 'This field is required'],
        has_quantity: [v => !!v || 'This field is required', v => v != 0 || 'This field is required'],
        has_price: [(v) => !!v || 'This field is required', (v) => v >= 0 || `Amount should be more than ${this.$currency.symbol} 0`],
        not_empty_select: [v => !!v || 'This field is required'],
      },
    }
  },
  computed: {
    ...mapGetters({
      company: 'getCompany',
    }),
    showDialog: {
      get () {
        return this.open 
      },
      set (newValue) {
        this.$emit('update:open', newValue)
      }
    },
  },
  methods: {
    ...mapActions(['showMessage']),
    async onFileChange (e) {
      const files = e.target.files || e.dataTransfer.files
      if (!files.length) {
        return
      }
      if (isFileSizeOverLimit(files[0], 5)) {
        this.showMessage(['error', this.$t('error_message.file_reached_limit')])
        return
      }
      this.selectedFile = files[0]
    },
    deleteFile () {
      this.selectedFile = null
    },
    done () {
      if (!this.$refs.form.validate()) {
        return
      }
      this.isLoading = true
      const formData = new FormData()
      formData.append('payment_method', this.paymentMethod)
      formData.append('paid_date', this.paidDate)
      formData.append('amount', Math.round(Number(this.amount) * this.$currency.multiplier))
      formData.append('remarks', this.remarks)
      if (this.selectedFile) {
        formData.append('file', this.selectedFile)
      }
      this.$axios.post(`/clinics/${this.company.slug}/inventory_invoices/${this.id}/payments`, formData)
        .then(resp => {
          this.$emit('onUploaded', resp.data)
          this.showDialog = false
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    getTodayDate () {
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },
    pickDate (field, picker, date) {
      this[field] = this.$options.filters.humanizeDate(date)
      this[picker] = false
    },
  }
}
</script>

<style lang="scss" scoped>
::v-deep {
  .v-text-field--outlined {
    fieldset {
      border-color: var(--v-neutral400);
    }
  } 
  .error--text {
    color: var(--v-danger500) !important;
    fieldset {
      border-color: var(--v-danger500);
    }
  }
  .date-field .v-input__append-inner {
    margin-top: 12px !important;
  }
}
.input-change-layout {
  position: absolute;
  top: 0px;
  left: 0px;
  right: 0px;
  bottom: 0px;
  opacity: 0;
}
</style>